/**
 * Interpolates zero values for missing entries in a sorted dataset.
 * Fills gaps in the data when entries are missing at expected intervals.
 */
export const interpolateZeroes = <T>({
    sortedData,
    keyExtractor,
    keyFormatter,
    createZeroEntry,
    expectedInterval,
    tolerance = expectedInterval / 2,
}: {
    sortedData: T[];
    keyExtractor: (item: T) => number | null;
    keyFormatter: (key: number) => string;
    createZeroEntry: (
        key: number,
        formattedKey: string,
        previousItem: T | undefined,
        nextItem: T | undefined
    ) => T;
    expectedInterval: number;
    tolerance?: number;
}): T[] => {
    if (sortedData.length === 0) {
        return [];
    }

    const result: T[] = [];
    for (let i = 0; i < sortedData.length; i++) {
        result.push(sortedData[i]);
        if (i < sortedData.length - 1) {
            const currentKey = keyExtractor(sortedData[i]);
            const nextKey = keyExtractor(sortedData[i + 1]);
            if (currentKey === null || nextKey === null) {
                continue;
            }

            const gap = nextKey - currentKey;
            if (gap > expectedInterval + tolerance) {
                const numMissing = Math.round(gap / expectedInterval) - 1;
                for (let j = 1; j <= numMissing; j++) {
                    result.push(
                        createZeroEntry(
                            currentKey + j * expectedInterval,
                            keyFormatter(currentKey + j * expectedInterval),
                            sortedData[i],
                            sortedData[i + 1]
                        )
                    );
                }
            }
        }
    }
    return result;
};
